/**
 * SP-8141 / SP-8647 — v2 `automatedParametersDocument` (nodes tree) codec.
 *
 * The sports-api backend (MR !1556) persists automated parameters as a versioned
 * document. v1 was a flat `conditions[]` list where each condition carried its own
 * `logicalOperator`, evaluated AND-before-OR. v2 replaces that with a recursive
 * `nodes` tree of `group` | `condition`, folded LEFT-TO-RIGHT with no implicit
 * precedence — grouping is explicit.
 *
 * The builder UI is a flat list of condition rows, each with its own AND/OR connector.
 * A contiguous run of rows can be wrapped in a single-level GROUP: the group is purely
 * a precedence bracket — every condition inside keeps its own operator. Groups do not
 * nest in the UI.
 *
 * Mapping onto v2:
 *  - Top level is a left-to-right chain of nodes: an ungrouped row is a bare
 *    `condition`; a user group is one node. Grouping never rewrites operators.
 *  - Because a v2 `group` node is homogeneous (one `innerOperator` folds all items),
 *    a bracket whose rows mix AND/OR is serialized as a nested homogeneous subtree via
 *    {@link buildLevel} (left-associative, matching the backend fold). That nesting is
 *    internal to the one user bracket — never surfaced as group-of-groups.
 *  - {@link documentToSeed} walks a v2 document back into the flat rows the editor
 *    seeds from, recovering each row's operator and which rows belong to a bracket.
 */

export type LogicalOperator = 'AND' | 'OR';

/** Leaf filter payload — matches sports-api `RestFilter` (sans logicalOperator). */
export type ConditionFilter = {
    type: string;
    name: string;
    values: string[];
};

export type ConditionNode = {
    kind: 'condition';
    /** How this node joins the previous sibling; absent on the first sibling. */
    joinOperator?: LogicalOperator;
    condition: ConditionFilter;
};

export type GroupNode = {
    kind: 'group';
    joinOperator?: LogicalOperator;
    /** How the group's own items are combined (homogeneous). */
    innerOperator: LogicalOperator;
    items: AutomatedParameterNode[];
};

export type AutomatedParameterNode = ConditionNode | GroupNode;

export const AUTOMATED_PARAMETERS_VERSION_2 = 2;

export type AutomatedParametersDocument = {
    version: number;
    nodes?: AutomatedParameterNode[];
    // v1 documents expose a flat conditions[] instead of nodes.
    conditions?: FlatCondition[];
};

/** A flat builder row: a leaf filter plus the operator joining it to the previous row. */
export type FlatCondition = ConditionFilter & {
    logicalOperator?: LogicalOperator | null;
};

/** A row the drawer is about to serialize: a leaf filter identified by its row key. */
export type BuilderRow = ConditionFilter & { rowKey: string };

/** The grouping state the drawer keeps alongside the rows. */
export type GroupingState = {
    /** row key → group (bracket) id; absent for ungrouped rows. */
    groupAssignments: Record<string, string>;
    /** row key → the operator joining it to the previous row. */
    logicalOperators: Record<string, LogicalOperator>;
};

/** A seed row produced from a stored document, tagged with its group (if any). */
export type SeedRow = FlatCondition & { groupId?: string };

export type BuilderSeed = {
    conditions: SeedRow[];
};

const asFilter = ({ type, name, values }: ConditionFilter): ConditionFilter => ({
    type,
    name,
    values,
});

/** A row inside a bracket, with the operator joining it to the previous row. */
type LevelRow = { filter: ConditionFilter; operator?: LogicalOperator };

/** Join two nodes with a homogeneous group, flattening a same-operator run into the
 *  left group so `A AND B AND C` is one AND group rather than nested pairs. */
const joinNodes = (
    left: AutomatedParameterNode,
    operator: LogicalOperator,
    right: AutomatedParameterNode,
): GroupNode => {
    if (left.kind === 'group' && left.innerOperator === operator) {
        return { ...left, items: [...left.items, right] };
    }
    return { kind: 'group', innerOperator: operator, items: [left, right] };
};

/** Fold a bracket's rows (left-to-right, per-row operator) into one v2 node. Mixed
 *  operators produce a nested homogeneous subtree; a uniform run stays flat. */
const buildLevel = (rows: LevelRow[]): AutomatedParameterNode => {
    let node: AutomatedParameterNode = {
        kind: 'condition',
        condition: rows[0].filter,
    };
    for (let i = 1; i < rows.length; i++) {
        node = joinNodes(node, rows[i].operator ?? 'AND', {
            kind: 'condition',
            condition: rows[i].filter,
        });
    }
    return node;
};

/** The exact inverse of {@link buildLevel}: unfold a subtree back to flat rows, each
 *  carrying the operator joining it to the previous row (null on the first). */
const flattenLevel = (node: AutomatedParameterNode): LevelRow[] => {
    if (node.kind === 'condition') {
        return [{ filter: node.condition }];
    }
    const operator = node.innerOperator;
    let rows = flattenLevel(node.items[0]);
    for (let i = 1; i < node.items.length; i++) {
        const sub = flattenLevel(node.items[i]);
        if (sub.length > 0) {
            sub[0] = { ...sub[0], operator };
        }
        rows = rows.concat(sub);
    }
    return rows;
};

type TopLevelItem =
    | { kind: 'condition'; row: BuilderRow }
    | { kind: 'group'; groupId: string; rows: BuilderRow[] };

/** Fold the ordered rows into top-level items (a bare row, or a run of same-group rows). */
const toTopLevelItems = (
    rows: BuilderRow[],
    groupAssignments: Record<string, string>,
): TopLevelItem[] => {
    const items: TopLevelItem[] = [];
    rows.forEach(row => {
        const groupId = groupAssignments[row.rowKey];
        const last = items[items.length - 1];
        if (groupId && last && last.kind === 'group' && last.groupId === groupId) {
            last.rows.push(row);
        } else if (groupId) {
            items.push({ kind: 'group', groupId, rows: [row] });
        } else {
            items.push({ kind: 'condition', row });
        }
    });
    return items;
};

/**
 * Ordered rows + grouping → v2 document. Ungrouped rows are bare `condition` nodes; a
 * grouped run is one node (its own homogeneous/nested subtree). Nodes join left-to-right
 * by the per-row operator of each item's first row (absent on the first). Returns `null`
 * for no rows so the caller omits the document (v2 requires non-empty nodes).
 */
export const buildAutomatedParametersDocument = (
    rows: BuilderRow[],
    { groupAssignments, logicalOperators }: GroupingState,
): AutomatedParametersDocument | null => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return null;
    }

    const items = toTopLevelItems(rows, groupAssignments);

    const nodes: AutomatedParameterNode[] = items.map((item, index) => {
        const firstRowKey =
            item.kind === 'condition' ? item.row.rowKey : item.rows[0].rowKey;
        const joinOperator: LogicalOperator | undefined =
            index === 0 ? undefined : logicalOperators[firstRowKey] ?? 'AND';

        let node: AutomatedParameterNode;
        if (item.kind === 'condition' || item.rows.length === 1) {
            const row = item.kind === 'condition' ? item.row : item.rows[0];
            node = { kind: 'condition', condition: asFilter(row) };
        } else {
            // Within a bracket the first row starts the fold; later rows join by their
            // own operator. (The first row's operator is the top-level connector above.)
            const levelRows: LevelRow[] = item.rows.map((row, rowIndex) => ({
                filter: asFilter(row),
                operator:
                    rowIndex === 0
                        ? undefined
                        : logicalOperators[row.rowKey] ?? 'AND',
            }));
            node = buildLevel(levelRows);
        }
        if (joinOperator) node.joinOperator = joinOperator;
        return node;
    });

    return { version: AUTOMATED_PARAMETERS_VERSION_2, nodes };
};

/**
 * v2 document → the flat rows the editor seeds from. Each row carries the operator
 * joining it to the previous row (null on the first) and, if it belongs to a bracket,
 * that bracket's id. A top-level group node is one bracket; its internal subtree is
 * unfolded back to per-row operators.
 */
export const documentToSeed = (
    document: AutomatedParametersDocument | null | undefined,
): BuilderSeed => {
    const conditions: SeedRow[] = [];
    if (!document || !Array.isArray(document.nodes)) {
        return { conditions };
    }

    let groupCounter = 0;
    document.nodes.forEach((node, index) => {
        const topJoin: LogicalOperator | null =
            index === 0 ? null : node.joinOperator ?? 'AND';

        if (node.kind === 'group') {
            const groupId = `g${++groupCounter}`;
            flattenLevel(node).forEach((levelRow, rowIndex) => {
                conditions.push({
                    ...levelRow.filter,
                    logicalOperator:
                        rowIndex === 0 ? topJoin : levelRow.operator ?? 'AND',
                    groupId,
                });
            });
            return;
        }

        conditions.push({ ...node.condition, logicalOperator: topJoin });
    });

    return { conditions };
};

/** True when the document is a v2 nodes tree (vs. a v1 flat conditions list). */
export const isVersion2Document = (
    document: AutomatedParametersDocument | null | undefined,
): boolean =>
    !!document &&
    document.version === AUTOMATED_PARAMETERS_VERSION_2 &&
    Array.isArray(document.nodes) &&
    document.nodes.length > 0;
